// Highcharts modules pro Reports stránku.
// Tři typy přes jeden modul (data-chart-type):
//   "horizontal-bar"  — Unikátní návštěvníci
//   "stacked-column"  — Kategorie nájemců
//   "device-split"    — Mobil vs. PC (100% stacked horizontal)

import Highcharts from 'highcharts/highstock'
import { formatNumber } from '../utils/Number'
import { highchartsOptions } from './chart-options'

Highcharts.setOptions(highchartsOptions)

const BRAND_COLORS = [
    '#4555FF',
    '#0DC1AE',
    '#F94D4D',
    '#FFAE41',
    '#24CB61',
    '#57BFFF',
    '#8B79FF',
    '#FF7848',
]

const buildHorizontalBar = (data) => {
    const hasYoY = data.labelYearAgo !== undefined
    const series = [{
        name: data.labelCurrent || 'Aktuální',
        color: BRAND_COLORS[0],
        // Každý sloupec nese barvu svého centra (#3732), aby šla vybraná centra
        // v porovnání rozeznat. Bez barvy se použije výchozí barva série.
        data: data.items.map(i => (i.color ? { y: i.value, color: i.color } : i.value)),
    }]
    if (hasYoY) {
        series.push({
            name: data.labelYearAgo,
            color: '#475569',
            data: data.items.map(i => i.valueYearAgo || 0),
        })
        series.push({
            name: data.labelTwoYearsAgo,
            color: '#94a3b8',
            data: data.items.map(i => i.valueTwoYearsAgo || 0),
        })
    }
    // grouped bar = each category needs extra vertical space
    const pointWidth = hasYoY ? 12 : 22
    const rowHeight = hasYoY ? 64 : 42
    return {
        chart: { type: 'bar', height: Math.max(360, data.items.length * rowHeight + 80), animation: false, zooming: { mouseWheel: false } },
        title: null,
        credits: { enabled: false },
        legend: { enabled: hasYoY },
        xAxis: {
            categories: data.items.map(i => i.label),
            labels: { style: { fontSize: '12px' } },
        },
        yAxis: {
            min: 0,
            title: null,
            gridLineColor: '#eee',
            labels: { formatter() { return formatNumber(this.value) } },
        },
        plotOptions: {
            bar: {
                borderWidth: 0,
                pointWidth,
                dataLabels: {
                    enabled: !hasYoY,
                    inside: false,
                    crop: false,
                    overflow: 'allow',
                    formatter() { return formatNumber(this.y) },
                    style: { fontSize: '12px', fontWeight: '600', textOutline: 'none' },
                },
            },
        },
        tooltip: {
            shared: hasYoY,
            formatter() {
                if (hasYoY) {
                    const rows = this.points.map(p => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${formatNumber(p.y)}</b>`)
                    return `<b>${this.x}</b><br>${rows.join('<br>')}`
                }
                return `<b>${this.x}</b>: ${formatNumber(this.y)}`
            },
        },
        series,
    }
}

const buildStackedColumn = (data) => ({
    chart: { type: 'column', height: 460, animation: false, zooming: { mouseWheel: false } },
    title: null,
    credits: { enabled: false },
    legend: { enabled: true, align: 'center', layout: 'horizontal', verticalAlign: 'bottom' },
    colors: BRAND_COLORS,
    xAxis: {
        categories: data.categories,
        labels: { style: { fontSize: '11px' }, rotation: -35 },
    },
    yAxis: {
        min: 0,
        title: null,
        gridLineColor: '#eee',
        stackLabels: {
            enabled: true,
            formatter() { return formatNumber(this.total) },
            style: { fontSize: '11px', fontWeight: 'normal', textOutline: 'none' },
        },
    },
    plotOptions: {
        column: {
            stacking: 'normal',
            borderWidth: 0,
            pointPadding: 0.05,
            groupPadding: 0.1,
        },
    },
    tooltip: {
        shared: true,
        formatter() {
            const rows = this.points.map(p => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${formatNumber(p.y)}</b>`)
            return `<b>${this.x}</b><br>${rows.join('<br>')}<br><i>Celkem: ${formatNumber(this.points[0].total)}</i>`
        },
    },
    series: data.series.map(s => ({ name: s.name, data: s.values })),
})

const buildDeviceSplit = (data) => ({
    chart: { type: 'bar', height: Math.max(280, data.categories.length * 28 + 80), animation: false, zooming: { mouseWheel: false } },
    title: null,
    credits: { enabled: false },
    legend: { enabled: true, align: 'center', layout: 'horizontal', verticalAlign: 'top' },
    colors: ['#4555FF', '#0DC1AE'],
    xAxis: {
        categories: data.categories,
        labels: { style: { fontSize: '12px' } },
    },
    yAxis: {
        min: 0,
        max: 100,
        title: null,
        gridLineColor: '#eee',
        labels: { formatter() { return this.value + ' %' } },
    },
    plotOptions: {
        bar: {
            stacking: 'percent',
            borderWidth: 0,
            pointWidth: 16,
            dataLabels: {
                enabled: true,
                formatter() { return Math.round(this.y) + ' %' },
                style: { fontSize: '11px', fontWeight: 'normal', textOutline: 'none', color: '#fff' },
            },
        },
    },
    tooltip: {
        shared: true,
        formatter() {
            const rows = this.points.map(p => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y.toFixed(1)} %</b>`)
            return `<b>${this.x}</b><br>${rows.join('<br>')}`
        },
    },
    series: [
        { name: 'Mobil', data: data.mobile },
        { name: 'PC', data: data.desktop },
    ],
})

const buildLine = (data) => {
    const series = [{
        name: data.labelCurrent || 'Aktuální období',
        type: 'area',
        ...(data.color ? { color: data.color } : {}),
        data: data.points.map(p => [new Date(p.date).getTime(), p.value]),
    }]
    // YoY série (#3650) — pokud jsou v datech, přidají se jako line bez fill
    if (data.pointsYearAgo) {
        series.push({
            name: data.labelYearAgo || 'Rok zpátky',
            type: 'line',
            data: data.pointsYearAgo.map(p => [new Date(p.date).getTime(), p.value]),
            dashStyle: 'ShortDash',
        })
    }
    if (data.pointsTwoYearsAgo) {
        series.push({
            name: data.labelTwoYearsAgo || '2 roky zpátky',
            type: 'line',
            data: data.pointsTwoYearsAgo.map(p => [new Date(p.date).getTime(), p.value]),
            dashStyle: 'Dot',
        })
    }
    const hasYoY = series.length > 1
    return {
        chart: { type: 'area', height: 360, animation: false, zooming: { mouseWheel: false } },
        title: null,
        credits: { enabled: false },
        legend: { enabled: hasYoY },
        colors: [BRAND_COLORS[0], '#475569', '#94a3b8'],
        xAxis: {
            type: 'datetime',
            labels: { style: { fontSize: '11px' } },
        },
        yAxis: {
            min: 0,
            title: null,
            gridLineColor: '#eee',
            labels: { formatter() { return formatNumber(this.value) } },
        },
        plotOptions: {
            area: {
                fillOpacity: 0.3,
                marker: { enabled: false, states: { hover: { enabled: true } } },
                lineWidth: 2,
            },
            line: {
                marker: { enabled: false, states: { hover: { enabled: true } } },
                lineWidth: 2,
            },
        },
        tooltip: {
            shared: hasYoY,
            formatter() {
                if (hasYoY) {
                    const rows = this.points.map(p => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${formatNumber(p.y)}</b>`)
                    return `<b>${Highcharts.dateFormat('%e. %m. %Y', this.x)}</b><br>${rows.join('<br>')}`
                }
                return `<b>${Highcharts.dateFormat('%e. %m. %Y', this.x)}</b><br>Návštěvníci: <b>${formatNumber(this.y)}</b>`
            },
        },
        series,
    }
}


const builders = {
    'horizontal-bar': buildHorizontalBar,
    'stacked-column': buildStackedColumn,
    'device-split': buildDeviceSplit,
    'line': buildLine,
}

const create = (element) => {
    const type = element.dataset.chartType
    const builder = builders[type]
    if (!builder) {
        console.warn(`chart-reports: unknown type "${type}"`)
        return
    }

    let data
    try {
        data = JSON.parse(element.dataset.chartData)
    } catch (e) {
        console.warn('chart-reports: invalid JSON', e)
        return
    }

    const options = builder(data)
    options.chart.renderTo = element

    Highcharts.chart(options)
}

document.querySelectorAll('.chart-reports').forEach(create)
